import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewChildren,
  computed,
  forwardRef,
  signal,
  inject,
} from '@angular/core';
import { CommonModule, ViewportScroller } from '@angular/common';
import { DonationRequestForm } from 'www-cms';
import { DirectusDirective } from 'directus';
import { BaseBuildingBlockComponent } from '../components/base-building-block.component';
import { LastItemBgClass } from '../../shell/shell.service';
import {
  FormsModule,
  ReactiveFormsModule,
  Validators,
  NonNullableFormBuilder,
  AbstractControl,
  ValidationErrors,
  FormGroup,
} from '@angular/forms';
import { ButtonComponent } from 'ui-elements/button';
import { FormFieldModule } from 'ui-elements/form-field';
import { BaseInputDirective } from 'ui-elements/form/input/base-input.directive';
import { FormFieldComponent } from 'ui-elements/form-field';
import {
  BehaviorSubject,
  Subject,
  catchError,
  debounceTime,
  filter,
  map,
  of,
  switchMap,
  takeUntil,
} from 'rxjs';
import {
  Address,
  CountryService,
  DonationService,
  S3FileUploadsService,
} from 'api';
import {
  DigitOnlyDirective,
  FeatureFlagService,
  ForbiddenDomainsValidatorDirective,
  CommonModule as SSCommonModule,
  addDaysToDate,
  getStencilDate,
  isDateAfterAnotherDay,
  isDateBeforeAnotherDay,
  regularExpressions,
  updateAllValueAndValidity,
  StateList,
} from 'common';
import { AddressFormComponent } from '../../shared/components/forms/address-form/address-form.component';
import { StepperComponent } from '../../shared/components/stepper/stepper.component';
import { StepComponent } from '../../shared/components/stepper/step/step.component';
import { StepHeaderComponent } from '../../shared/components/stepper/step-header/step-header.component';
import { NotificationModalServiceService } from '../../shared/services/notification-modal-service.service';
import { controlValuesMatchValidator } from 'checkout';
import { FormErrorListingComponent } from '../../shared/components/form-error-listing/form-error-listing.component';
import { VerifyAddressModalService } from '../../shared/components/verify-address-modal/verify-address-modal.service';
import { LoadingModalComponent } from '../../shared/components/loading-modal/loading-modal.component';
import { Router } from '@angular/router';
import { AnalyticsService } from '../../shared/services/analytics.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { SelectData, ssSelectImports } from 'ui-elements/select';
import { NgxMaskDirective } from 'ngx-mask';
import { SegmentIdentifyTraits, SegmentService } from 'segment';

export const lessThanSixMonths = (
  abstractControl: AbstractControl
): ValidationErrors | null => {
  return isDateBeforeAnotherDay(
    new Date(abstractControl.value),
    addDaysToDate(new Date(), 182)
  )
    ? null
    : { lessThanSixMonths: true };
};

export const moreThanFiveWeeks = (
  abstractControl: AbstractControl
): ValidationErrors | null => {
  return isDateAfterAnotherDay(
    new Date(abstractControl.value),
    addDaysToDate(new Date(), 35)
  )
    ? null
    : { moreThanFiveWeeks: true };
};

@Component({
  selector: 'ss-www-donation-request-form',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    FormFieldModule,
    ButtonComponent,
    SSCommonModule,
    BaseInputDirective,
    AddressFormComponent,
    StepperComponent,
    StepComponent,
    StepHeaderComponent,
    ForbiddenDomainsValidatorDirective,
    DigitOnlyDirective,
    FormErrorListingComponent,
    LoadingModalComponent,
    ssSelectImports,
    NgxMaskDirective,
    DirectusDirective,
  ],
  providers: [VerifyAddressModalService],
  templateUrl: './donation-form.component.html',
  styleUrls: ['./donation-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DonationFormComponent
  extends BaseBuildingBlockComponent
  implements OnDestroy, OnInit
{
  private countryService = inject(CountryService);
  private formBuilder = inject(NonNullableFormBuilder);
  private notificationModal = inject(NotificationModalServiceService);
  private donationService = inject(DonationService);
  private viewportScroller = inject(ViewportScroller);
  private verifyAddressModalService = inject(VerifyAddressModalService);
  private notificationService = inject(NotificationModalServiceService);
  private router = inject(Router);
  private analyticsService = inject(AnalyticsService);
  private featureFlagService = inject(FeatureFlagService);
  private s3Upload = inject(S3FileUploadsService);
  private readonly segmentService = inject(SegmentService);

  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
  @ViewChild(forwardRef(() => AddressFormComponent))
  addressForm!: AddressFormComponent;
  @ViewChildren(forwardRef(() => FormFieldComponent))
  formFields?: FormFieldComponent[];
  @Input({ required: true }) donationRequestFormData!: DonationRequestForm;
  stateList = StateList;
  states$ = new BehaviorSubject<SelectData<string>[]>([]);
  countries$ = new BehaviorSubject<SelectData<string>[]>([]);
  shows$ = new BehaviorSubject<SelectData[]>([]);
  errors = signal<string[]>([]);
  form = this.initForm();
  onDestroy$ = new Subject<void>();
  isSubmitting = signal<boolean>(false);
  isLocationRequiredFlag$ = this.featureFlagService.getValue(
    'isDonationFormLocationRequired',
    false
  );
  isLocationRequired = toSignal(this.isLocationRequiredFlag$);

  theaterLocations: SelectData<string>[] = [
    { value: 'STRASBURG_PA', name: 'Lancaster, PA' },
    { value: 'BRANSON_MO', name: 'Branson, MO' },
  ];

  today = signal<Date>(new Date());
  minDate = computed(() => {
    const date = this.today();
    return getStencilDate(addDaysToDate(date, 37));
  });
  maxDate = computed(() => {
    const date = this.today();
    return getStencilDate(addDaysToDate(date, 181));
  });

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  ngOnInit(): void {
    this.countryService
      .getCountries()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((countries) => {
        this.countries$.next(
          countries.map((key) => ({ value: key.code, name: key.name }))
        );
      });

    this.eventDateControl.valueChanges
      .pipe(
        filter(() => this.eventDateControl.valid),
        debounceTime(2000),
        map((date) => {
          const selectedDate = new Date(date);
          return selectedDate.toISOString().split('T')[0];
        }),
        switchMap((isoDate) =>
          this.donationService.getShowsForDonation(isoDate as string)
        ),
        catchError(() => []),
        takeUntil(this.onDestroy$)
      )
      .subscribe((shows) =>
        this.shows$.next(
          shows.events.map((event) => ({
            value: event.eventCode,
            name: event.eventName,
          }))
        )
      );

    // Add validators to confirm email fields
    // Needs to be after init because FormBuilder doesn't create
    // the controls until after the form is created.
    this.contactEmailConfirmControl.addValidators(
      controlValuesMatchValidator(this.contactEmailControl)
    );

    this.organizationEmailConfirmControl.addValidators(
      controlValuesMatchValidator(this.organizationEmailControl)
    );

    this.isLocationRequiredFlag$
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((flag) => {
        const detailsGroup: FormGroup = this.form.controls.details;

        if (!flag) {
          detailsGroup.controls['eventCode'].setValidators(Validators.required);
          detailsGroup.controls['eventCode'].updateValueAndValidity();
          detailsGroup.removeControl('location');
          return;
        }
        detailsGroup.controls['location'].setValidators(Validators.required);
        detailsGroup.controls['location'].updateValueAndValidity();
        detailsGroup.removeControl('eventCode');
      });
  }

  override getBgClass(): LastItemBgClass {
    return 'bg-white';
  }
  onFileInput(): void {
    if (
      this.fileInput.nativeElement.files?.[0]?.size &&
      this.fileInput.nativeElement.files[0].size > 4.5e6
    ) {
      this.notificationModal.showMessage({
        title: 'File too large',
        message:
          'Request letter size cannot exceed 4.5MB in size. Please try a smaller file.\nYou can try removing images to reduce the size.',
      });
      this.fileInput.nativeElement.value = '';
      this.form.controls.letter.setValue('');
    }
  }

  onSubmit() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      updateAllValueAndValidity(Object.values(this.form.controls));
      const formErrors =
        this.formFields?.map((field) => field.allErrorText).flat() ?? [];
      formErrors.push(
        ...(this.addressForm.formFields
          ?.map((field) => field.allErrorText)
          .flat() ?? [])
      );
      this.errors.set(formErrors);
      this.viewportScroller.scrollToAnchor('donation-form');
      return;
    }

    this.isSubmitting.set(true);

    this.verifyAddressModalService
      .open(this.form.controls.customer.controls.address.value as Address)
      .pipe(
        map((address) => ({
          ...this.form.value,
          customer: { ...this.form.value.customer, address },
          details: {
            ...this.form.value.details,
            eventDate: new Date(
              this.form.value.details!.eventDate!
            ).toISOString(),
          },
        })),
        map((data) => {
          const file = this.fileInput.nativeElement.files![0];
          const fileName = `request.${file.name.split('.').pop()}`;

          if (!this.isLocationRequired()) {
            const formData = new FormData();
            new FormData().append('donationRequest', JSON.stringify(data));
            formData.append('file', file, fileName);
            formData.append(
              'donationRequest',
              new Blob([JSON.stringify(data)], { type: 'application/json' })
            );
            return formData;
          }

          return { ...data, details: { ...data.details, fileName } };
        }),
        switchMap((formData) =>
          this.donationService.submitDonationRequest(formData)
        ),
        switchMap((presignedFormData) => {
          if (!this.isLocationRequired()) return of(null);
          const formData = new FormData();
          const fields = presignedFormData.fields;
          const file = this.fileInput.nativeElement.files![0];

          Object.entries(fields).forEach(([key, value]) => {
            formData.append(key, value);
          });
          formData.append('file', file);

          return this.s3Upload.uploadFileToS3(presignedFormData.url, formData);
        }),
        takeUntil(this.onDestroy$)
      )
      .subscribe({
        next: () => {
          // Segment identity tracking traits:
          // https://www.twilio.com/docs/segment/connections/spec/identify#custom-traits
          this.identifyAndTrackEvent();
          this.isSubmitting.set(false);
          this.analyticsService.trackSubmitDonationRequest();
          this.notificationService.showMessage({
            title: 'Donation request submitted',
            message:
              'Thank you for submitting your donation request.\nWe will review your request, and you can expect to hear from us within 4-6 weeks.',
          });
          this.router.navigate(['/']);
        },
        error: (error) => {
          this.notificationService.showMessage({
            title: 'Error submitting donation request',
            message: error.error?.message ?? error.message ?? 'Unknown error',
          });
          this.isSubmitting.set(false);
        },
      });
  }

  private identifyAndTrackEvent() {
    const customer = this.form.controls.customer;
    const addressControl = customer.controls.address.value;
    const primaryContactControl = customer.controls.primaryContact.controls;
    const traits: SegmentIdentifyTraits = {
      firstName: primaryContactControl.firstName.value,
      lastName: primaryContactControl.lastName.value,
      phone: primaryContactControl.phoneNumber.value,
      email: primaryContactControl.emailAddress.value,
      company: {
        name: customer.controls.organizationContact.controls.name.value,
      },
      address: {
        street: addressControl?.addressLine1,
        city: addressControl?.city,
        state: addressControl?.state,
        country: addressControl?.country,
        postalCode: addressControl?.zipcode,
      },
    };
    this.segmentService.identifyUser(undefined, traits);
    this.segmentService.trackEvent('Form Submitted', {
      traits,
      form: 'Donation Request',
    });
  }

  public get eventDateControl() {
    return this.form.controls.details.controls.eventDate;
  }

  private get contactEmailControl() {
    return this.form.controls.customer.controls.primaryContact.controls
      .emailAddress;
  }

  private get contactEmailConfirmControl() {
    return this.form.controls.customer.controls.primaryContact.controls
      .emailAddressConfirm;
  }

  private get organizationEmailControl() {
    return this.form.controls.customer.controls.organizationContact.controls
      .emailAddress;
  }

  private get organizationEmailConfirmControl() {
    return this.form.controls.customer.controls.organizationContact.controls
      .emailAddressConfirm;
  }

  private initForm() {
    return this.formBuilder.group({
      customer: this.formBuilder.group({
        primaryContact: this.formBuilder.group({
          firstName: [
            '',
            [
              Validators.required,
              Validators.pattern(regularExpressions.namePatten),
              Validators.maxLength(50),
            ],
          ],
          lastName: [
            '',
            [
              Validators.required,
              Validators.pattern(regularExpressions.namePatten),
              Validators.maxLength(50),
            ],
          ],
          phoneNumber: [
            '',
            [
              Validators.required,
              Validators.minLength(10),
              Validators.maxLength(20),
            ],
          ],
          emailAddress: ['', [Validators.required, Validators.maxLength(50)]], // ssForbidden has email pattern
          emailAddressConfirm: ['', [Validators.required]], // emails match validator added on init
        }),
        address: this.formBuilder.group({
          addressLine1: [
            '',
            [
              Validators.required,
              Validators.minLength(3),
              Validators.maxLength(50),
            ],
          ],
          addressLine2: ['', Validators.maxLength(30)],
          city: ['', [Validators.required, Validators.maxLength(30)]],
          state: ['', [Validators.required, Validators.maxLength(50)]],
          country: ['USA', Validators.required],
          zipcode: ['', [Validators.required, Validators.maxLength(10)]],
        }),
        organizationContact: this.formBuilder.group({
          name: ['', [Validators.required, Validators.maxLength(50)]],
          phoneNumber: [
            '',
            [
              Validators.required,
              Validators.minLength(10),
              Validators.maxLength(20),
            ],
          ],
          emailAddress: ['', [Validators.required, Validators.maxLength(50)]], // ssForbidden has email pattern
          emailAddressConfirm: ['', [Validators.required]],
        }),
      }),
      details: this.formBuilder.group({
        eventDate: [
          '',
          [Validators.required, lessThanSixMonths, moreThanFiveWeeks],
        ],
        eventName: ['', [Validators.required, Validators.maxLength(50)]],
        eventCode: [''], // Setting validation onInit
        location: [''], // Setting validation onInit
        usedFor: ['', [Validators.required, Validators.maxLength(200)]],
        comments: ['', Validators.maxLength(100)],
      }),
      letter: this.formBuilder.control('', Validators.required),
    });
  }
}
